import { getTheme, IButtonStyles, ITheme } from '@fluentui/react';
import AppContext, { IAppContext } from 'App/AppContext';
import { IPActionButton, IPFontIcon, IPLink, IPText, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import HrefLink from 'components/Links/HrefLink';
import { Info } from 'components/Notification/Info';
import { globalBlogNIS2Url, globalKB_Demo, globalKB_DemoNIS2, globalKB_ISOBasicInfo } from 'globalConstants';
import {
  globalBoxBorderRadius,
  globalFontBoldWeight,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
} from 'globalStyles';
import { TagSystemTypes } from 'models/tag';
import { TenantOnboarding, TenantOnboardingSteps } from 'models/tenant';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FeatureTypes, hasUserFeature, hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import { IPSharedColors } from 'themes/runtime/fluentColors';
import { SubscriptionTypes } from 'utils/subscription';
import { navigateToExternalUrl } from 'utils/url';
import { isProjectsModuleEnabledForTenant } from 'utils/projects';
import { getTrialWelcomePanelChrome } from './TrialWelcomeNextSteps';

interface INextStepActionsProps {
  onboardingSteps: TenantOnboardingSteps;
}

const navigateToKB = (kbLink: string) => {
  navigateToExternalUrl(kbLink, undefined, '', true);
};

const getQuickRowStyle = (appContext: IAppContext, theme: ITheme) => {
  return {
    root: {
      ...getTrialWelcomePanelChrome(appContext, theme),
      borderRadius: globalBoxBorderRadius,
      paddingBottom: 20,
      paddingLeft: 20,
      paddingRight: 20,
      paddingTop: 20,
      width: '100%',
    },
  };
};

const getQuickRowTitleStyle = (theme: ITheme) => {
  return {
    root: {
      color: theme.palette.neutralSecondary,
      fontWeight: globalFontBoldWeight,
      textTransform: 'uppercase',
    },
  };
};

const getQuickActionButtonStyles = (appContext: IAppContext, theme: ITheme): IButtonStyles => {
  const background = appContext.useDarkMode ? 'transparent' : theme.palette.white;
  const borderColor = appContext.useDarkMode ? theme.palette.neutralTertiary : theme.palette.themeLight;
  const color = appContext.useDarkMode ? theme.palette.neutralLighter : theme.palette.themeDarker;
  const hoverBackground = appContext.useDarkMode ? theme.palette.neutralPrimaryAlt : theme.palette.themeLighterAlt;
  const pressedBackground = appContext.useDarkMode ? theme.palette.neutralPrimary : theme.palette.themeLight;
  const disabledColor = appContext.useDarkMode ? theme.palette.neutralTertiary : theme.palette.neutralSecondary;

  return {
    root: {
      background,
      border: `1px solid ${borderColor}`,
      borderColor,
      borderRadius: globalBoxBorderRadius,
      color,
      height: 32,
      paddingLeft: 8,
      paddingRight: 10,
    },
    rootHovered: {
      background: hoverBackground,
      borderColor,
      color,
    },
    rootPressed: {
      background: pressedBackground,
      borderColor,
      color,
    },
    rootDisabled: {
      background,
      border: `1px solid ${borderColor}`,
      borderColor,
      color: disabledColor,
      selectors: {
        '.ms-Button-icon': {
          color: disabledColor,
        },
        '.ms-Button-label': {
          color: disabledColor,
        },
      },
    },
    icon: {
      color,
    },
    iconHovered: {
      color,
    },
    iconPressed: {
      color,
    },
    iconDisabled: {
      color: disabledColor,
    },
    label: {
      color,
      fontWeight: globalFontBoldWeight,
    },
  };
};

const getProjectLink = (appContext: IAppContext): string => {
  const groupNames = appContext.globalDataCache.tags.getGroupNamesForSystemType(TagSystemTypes.Project);

  return `/tasks/alltasks?view=timeline&tagGroups=${groupNames.join(',')}`;
};

export const RenderQuickActionButtons = (props: INextStepActionsProps) => {
  const { t } = useTranslation(['translation', 'dashboard', 'adminCompany']);
  const appContext = useContext(AppContext);
  const theme = getTheme();
  const history = useHistory();
  const quickActionButtonStyles = getQuickActionButtonStyles(appContext, theme);
  const isAdmin = appContext.user.isAdmin();
  const adminOnlyTooltip = isAdmin ? undefined : t('translation:General.Notifications.ContactAdministratorForFeature');
  const canUseProjects =
    isProjectsModuleEnabledForTenant(appContext.globalDataCache.settings) &&
    hasUserFeature(appContext, FeatureTypes.Projects);

  return (
    <IPStack tokens={globalStackTokensGapMedium} styles={getQuickRowStyle(appContext, theme)}>
      <IPStack.Item>
        <IPText block variant="small" styles={getQuickRowTitleStyle(theme)}>
          {t('dashboard:TrialWelcome.NextSteps.ActionRows.Buttons.Title')}
        </IPText>
      </IPStack.Item>
      <IPStack horizontal wrap horizontalAlign="center" verticalAlign="center" tokens={globalStackTokensGapSmall}>
        <IPActionButton
          iconProps={{ iconName: 'TimelineProgress' }}
          styles={quickActionButtonStyles}
          text={t('dashboard:TrialWelcome.NextSteps.ActionRows.Buttons.ProjectMilestones')}
          onClick={() => history.push(getProjectLink(appContext))}
        />
        {canUseProjects && (
          <IPActionButton
            iconProps={{ iconName: 'MapDirections' }}
            styles={quickActionButtonStyles}
            text={t('translation:NavMain.Project')}
            onClick={() => history.push('/project')}
          />
        )}
        <IPTooltipHost content={adminOnlyTooltip}>
          <IPActionButton
            iconProps={{ iconName: 'AddFriend' }}
            styles={quickActionButtonStyles}
            text={t('dashboard:TrialWelcome.NextSteps.ActionRows.Buttons.InviteColleagues')}
            disabled={!isAdmin}
            onClick={() => history.push('/admin/users/licensing#add')}
          />
        </IPTooltipHost>
        <IPActionButton
          iconProps={{ iconName: 'PlugConnected' }}
          styles={quickActionButtonStyles}
          text={t('dashboard:TrialWelcome.NextSteps.ActionRows.Buttons.AuthorizeIntegrations')}
          onClick={() => history.push('/admin/settings/integration')}
        />
      </IPStack>
    </IPStack>
  );
};

interface IRenderQuickLinksProps {
  onContactServiceDesk: () => void;
}

export const RenderQuickLinks = (props: IRenderQuickLinksProps) => {
  const { t } = useTranslation(['translation', 'dashboard', 'adminCompany']);
  const appContext = useContext(AppContext);
  const theme = getTheme();
  const isAdmin = appContext.user.isAdmin();
  const adminOnlyTooltip = isAdmin ? undefined : t('translation:General.Notifications.ContactAdministratorForFeature');
  const subscriptionType = appContext.user.tenant.subscriptionType;
  const demoUrl = subscriptionType === SubscriptionTypes.NIS2 ? globalKB_DemoNIS2 : globalKB_Demo;
  const knowledgeBaseUrl = subscriptionType === SubscriptionTypes.NIS2 ? globalBlogNIS2Url : globalKB_ISOBasicInfo;
  const knowledgeBaseIsExternal = subscriptionType === SubscriptionTypes.NIS2;

  return (
    <IPStack tokens={globalStackTokensGapMedium} styles={getQuickRowStyle(appContext, theme)}>
      <IPStack.Item>
        <IPText block variant="small" styles={getQuickRowTitleStyle(theme)}>
          {t('dashboard:TrialWelcome.NextSteps.ActionRows.Links.Title')}
        </IPText>
      </IPStack.Item>
      <IPStack horizontal wrap horizontalAlign="center" verticalAlign="center" tokens={globalStackTokensGapMedium}>
        <IPTooltipHost content={adminOnlyTooltip}>
          <IPText>
            <HrefLink disabled={!isAdmin} href={isAdmin ? '/admin/subscription' : undefined}>
              {t('dashboard:TrialWelcome.NextSteps.ActionRows.Links.Subscription')}
            </HrefLink>
          </IPText>
        </IPTooltipHost>
        <IPText>
          <HrefLink external newTab href={demoUrl} onClick={() => navigateToExternalUrl(demoUrl, undefined, '', true)}>
            {t('dashboard:TrialWelcome.NextSteps.ActionRows.Links.Demo')}
          </HrefLink>
        </IPText>
        <IPText>
          <HrefLink
            href={knowledgeBaseUrl}
            external={knowledgeBaseIsExternal}
            newTab={knowledgeBaseIsExternal}
            onClick={() => {
              if (knowledgeBaseIsExternal) {
                navigateToExternalUrl(globalBlogNIS2Url, undefined, '', true);
              } else {
                navigateToKB(globalKB_ISOBasicInfo);
              }
            }}
          >
            {t('dashboard:TrialWelcome.NextSteps.ActionRows.Links.KnowledgeBase')}
          </HrefLink>
        </IPText>
        <IPText>
          <IPLink onClick={() => props.onContactServiceDesk()}>
            {t('dashboard:TrialWelcome.NextSteps.ActionRows.Links.ServiceDesk')}
          </IPLink>
        </IPText>
      </IPStack>
    </IPStack>
  );
};

//
// RenderDone
//
interface IRenderDoneProps {
  onboardingState: TenantOnboarding;
  updateOnboardingState: (onboardingState: TenantOnboarding) => Promise<void>;
}

export const RenderDone = (props: IRenderDoneProps) => {
  const { t } = useTranslation(['translation', 'dashboard', 'adminCompany']);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const canStartOnboardingAgain = appContext.user.login.isInTrial() && hasUserFeatureGenericManager(appContext);

  const startOnboardingAgain = async () => {
    const newOnboardingState = props.onboardingState.clone();
    newOnboardingState.onboardingSteps.currentStep = 0;
    await props.updateOnboardingState(newOnboardingState);
    appContext.setOnboardingInProgress(true);
    appContext.copilotBridge.isOpen = false;
    history.push('/reload');
    history.goBack();
  };

  if (appContext.isMobileView) return null;

  const doneItems = [
    t('dashboard:TrialWelcome.NextSteps.Done.Item3'),
    t('dashboard:TrialWelcome.NextSteps.Done.Item4'),
    t('dashboard:TrialWelcome.NextSteps.Done.Item5'),
  ];

  const doneInfo = (
    <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 5 } }}>
      {doneItems.map((item) => (
        <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall} key={item}>
          <IPFontIcon iconName="SkypeCircleCheck" style={{ fontSize: '14px', color: IPSharedColors.green20 }} />
          <IPText variant="small">{item}</IPText>
        </IPStack>
      ))}
    </IPStack>
  );

  //
  // Main render
  //
  if (
    !canStartOnboardingAgain &&
    !props.onboardingState.onboardingSteps.languageIsSet &&
    (props.onboardingState.onboardingSteps.certifiedStandards?.length ?? 0) === 0 &&
    (props.onboardingState.onboardingSteps.newStandards?.length ?? 0) === 0 &&
    !props.onboardingState.onboardingSteps.previewPackageInstalled &&
    !props.onboardingState.onboardingSteps.projectMilestonesCreated &&
    !props.onboardingState.onboardingSteps.projectDocument
  ) {
    return null;
  }

  return (
    <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
      {canStartOnboardingAgain && (
        <IPText variant="small">
          <IPLink onClick={() => startOnboardingAgain()}>
            {t('dashboard:TrialWelcome.NextSteps.Summary.StartAgainLink')}
          </IPLink>
        </IPText>
      )}
      <Info message={doneInfo} />
    </IPStack>
  );
};
